<template>
  <b-card
    class="al-card"
    :class="[{ 'is-small': isSmall }, { 'is-white': isWhite }, colorStyle]"
    :title="title"
    :sub-title="subTitle"
    :img-src="imgSrc"
    :img-alt="imgAlt"
    :img-top="imgTop"
    :img-bottom="imgBottom"
    :img-left="imgLeft"
    :img-right="imgRight"
    :overlay="overlay"
    :no-body="noBody"
    test-id="al-card"
    @click="$emit('click', $event)"
  >
    <template v-if="hasHeaderSlot()" slot="header">
      <!-- @slot Use this slot For custom rendering of header content -->
      <slot name="header" />
    </template>

    <!-- @slot Use this for custom rendering of body content -->
    <template v-if="(noBody && bgImage) || (noBody && bgColor)">
      <b-card-body
        :class="[{ 'al-card-bg-image': bgImage }, { 'al-card-bg-color': bgColor }]"
        :style="`background-image: url('${bgImage}'); background-color: ${bgColor}`"
      >
        <div class="al-card-body-content">
          <slot />
        </div>
      </b-card-body>
    </template>
    <template v-else>
      <slot />
    </template>

    <template v-if="hasFooterSlot()" slot="footer">
      <!-- @slot Use this slot for custom rendering of footer content-->
      <slot name="footer" />
    </template>
  </b-card>
</template>

<script>
import { BCard } from 'bootstrap-vue';
/**
 * A card is a content container encapsulated in a border rounded box
 * with header and footer sections.
 *
 * @displayName AlCard
 */
export default {
  name: 'AlCard',
  components: {
    BCard
  },
  props: {
    /**  Text content to place in the header */
    title: {
      type: String,
      required: false,
      default: ''
    },
    /**  Text subtitle to place in the header */
    subTitle: {
      type: String,
      required: false,
      default: ''
    },
    /**  Image to place in the header */
    imgSrc: {
      type: String,
      required: false,
      default: ''
    },
    /**  Header image alt text */
    imgAlt: {
      type: String,
      required: false,
      default: ''
    },
    /**  Set if the image should appear at the top of the card */
    imgTop: {
      type: Boolean,
      default: false
    },
    /**  Set if the image should appear at the start (left)
     * of the card. Synonym for the 'left' prop */
    imgLeft: {
      type: Boolean,
      default: false
    },
    /**  Set if the image should appear at the bottom of the card */
    imgBottom: {
      type: Boolean,
      default: false
    },
    /**  Set if the image should appear at the end (right) of the card.
     * Synonym for the 'right' prop */
    imgRight: {
      type: Boolean,
      default: false
    },
    /**  When set, will overlay the card body on top of the image (if the card has an image) */
    overlay: {
      type: Boolean,
      default: false
    },
    /** Disable rendering of the default inner card-body element */
    noBody: {
      type: Boolean,
      default: false
    },
    /**  When set, will overlay the card spaces are more small */
    isSmall: {
      type: Boolean,
      default: false
    } /**  When set, will overlay the card has not background */,
    isWhite: {
      type: Boolean,
      default: false
    },
    /**
     * When set, will determine the card general color scheme that it can be one of the folowings:
     * "red", "green", "blue", "purple", "yellow", "pink"
     */
    color: {
      type: String,
      default: ''
    },
    /**  Image to bg in the body */
    bgImage: {
      type: String,
      required: false,
      default: ''
    },
    /**  Background to body */
    bgColor: {
      type: String,
      required: false,
      default: ''
    }
  },
  data() {
    return {
      styles: ['card-red', 'card-green', 'card-blue', 'card-purple', 'card-yellow', 'card-pink']
    };
  },
  computed: {
    colorStyle() {
      return `card-${this.color}`;
    }
  },
  methods: {
    hasHeaderSlot() {
      return this.$slots.header;
    },
    hasFooterSlot() {
      return this.$slots.footer;
    }
  }
};
</script>

<style lang="scss">
$black: color(black, lighten, 3);

$color-map: (
  red: #cf5a3e,
  green: #05332f,
  blue: #00375c,
  skyblue: #0b606e,
  purple: #403e71,
  yellow: #edc247,
  pink: #f09c9e
);

$color-map-light: (
  'red': #ffd6cc,
  'green': #d2eed3,
  'blue': #adcade,
  'skyblue': #cef4fa,
  'purple': #d0cfe3,
  'yellow': #fff6de,
  'pink': #fff2f2
);

/**
 * Variables
 */
$bg-header: #f5f8f7;
$bg-color: #fff;
$bg-color-footer: #f5f8f7;
$br-color: rgba(#979797, 0.22);
$pd-header: 28px 32px;
$pd-body: 28px 32px;
$pd-footer: 28px 32px;
$br-radius: 4px;
$box-shadow: 0 8px 4px -4px rgba(54, 64, 63, 0.1);

// is-small
$pd-header-small: 18px 32px;
$pd-body-small: 18px 32px;
$pd-footer-small: 18px 32px;
$padding: 24px;

//header
$h-card-header: 60px;
$h-card-header-lg: 92px;
/**
 * Styles for card
 */
.al-card {
  border: 1px solid $br-color;
  border-radius: $br-radius;
  box-shadow: $box-shadow;
  overflow: hidden;

  &.no-shadow {
    box-shadow: none;
  }

  .card-header {
    align-items: center;
    background: transparent;
    color: #a4a4a4;
    font-size: 14px;
    height: $h-card-header;
    padding: $padding;
  }

  .card-body {
    padding: $pd-body;

    h5 {
      -webkit-box-orient: vertical;
      display: -webkit-box;
      font-size: 20px;
      font-weight: bold;
      -webkit-line-clamp: 3;
      line-height: 28px;
      overflow: hidden;
    }

    &.al-card-bg-image {
      background-position: center center;
      background-repeat: no-repeat;
      background-size: cover;
      position: relative;

      .al-card-body-content {
        position: relative;
        z-index: 2;
      }

      &::before {
        background-color: rgba(0, 0, 0, 0.5);
        content: '';
        height: 100%;
        left: 0;
        position: absolute;
        top: 0;
        width: 100%;
        z-index: 1;
      }
    }
  }

  .card-footer {
    background-color: $bg-color-footer;
    border-top: 1px solid $br-color;
    padding: $pd-footer;
  }

  &.is-small {
    .card-header {
      padding: $pd-header-small;
    }

    .card-body {
      padding: $pd-body-small;
    }

    .card-footer {
      padding: $pd-footer-small;
    }
  }

  &.is-white {
    .card-header {
      background-color: $bg-color;
    }

    .card-body {
      padding: $pd-body-small;
    }

    .card-footer {
      background-color: $bg-color;
    }
  }

  &.is-card-auto {
    .card-header {
      height: $h-card-header-lg;
    }
  }
}

@each $name, $color in $color-map {
  .al-card.card-#{'' + $name + ''} {
    $light-color: rgba($color, 0.2);
    position: relative;

    .card-body,
    .card-header {
      background-color: map-get($color-map-light, #{$name});
    }

    .is-path-color {
      * {
        color: inherit;
      }
    }

    .card-header,
    .card-body,
    .is-path-color {
      color: $color;
    }

    .al-badge {
      background: transparent;
      border: 1px solid $color;
    }

    .al-badge:hover {
      color: $color;
    }
  }
}
</style>
